import { select, selectAll, on, toggleClass, hasClass, removeAttribute } from 'helpers/dom'
import { map } from 'helpers/utils'
import {
  resolveActiveWarehouse,
  parseStockJson,
  getMetafieldKey,
  isSellable,
  isPreorderStatus,
  classifyWarehouseStatus,
  combineWarehouseVerdicts,
  STORAGE_1P_KEY,
  STORAGE_2P_KEY
} from 'helpers/stock-status'
import { getBundleStockRollup, parseBundleComposition } from 'helpers/bundle-items'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'

const selectors = {
  MODAL_ZOOM_SELECTOR: 'modal-dialog',
  GALLERY_ITEM_SELECTOR: 'swiper-slide',
  GALLERY_ZOOM_MAIN_SELECTOR: '.js-gallery-zoom-main swiper-container',
  GALLERY_ZOOM_THUMB_ITEM_SELECTOR: '.js-zoom-thumb-item',
  GALLERY_THUMB_ACTIVE_CLASS: 'swiper-slide-thumb-active',
  GALLERY_ZOOM_MAIN_ITEMS: '.js-main-gallery-zoom-item',
  GALLERY_MAIN_CAROUSEL_DESKTOP: 'swiper-container.carousel-swiper-gallery-main',
  GALLERY_MAIN_CAROUSEL_MOBILE: 'swiper-container.carousel-swiper-gallery-mobile',
  GALLERY_THUMBNAIL_CAROUSEL: 'swiper-container.carousel-swiper-gallery-thumbnails',
  GALLERY_NEXT_SELECTOR: '.js-carousel-next',
  GALLERY_PREV_SELECTOR: '.js-carousel-prev',
  THUMBNAIL_NEXT_SELECTOR: '.js-thumbnail-next',
  THUMBNAIL_PREV_SELECTOR: '.js-thumbnail-prev',
  WISHLIST_BUTTON: '.js-gallery-wishlist-btn'
}

// Keyed by the badgeStatus a bundle rollup returns.
const BUNDLE_BADGES = {
  'Ready to Ship': { text: 'Ready to Ship', cls: 'badge-label-rts' },
  'Pre-order': { text: 'Pre-order', cls: 'badge-label-preorder' }
}

export default class ProductGallery extends HTMLElement {
  constructor() {
    super()

    this.modalZoom = select(selectors.MODAL_ZOOM_SELECTOR, this)
    this.mainGalleryZoom = select(selectors.GALLERY_ZOOM_MAIN_SELECTOR, this)
    this.mainGalleryItems = selectAll(selectors.GALLERY_ZOOM_MAIN_ITEMS, this)
    this.mainCarouselDesktop = select(selectors.GALLERY_MAIN_CAROUSEL_DESKTOP, this)
    this.mainCarouselMobile = select(selectors.GALLERY_MAIN_CAROUSEL_MOBILE, this)
    this.thumbnailCarousel = select(selectors.GALLERY_THUMBNAIL_CAROUSEL, this)
    this.wishlistButton = select(selectors.WISHLIST_BUTTON, this)
    this.nextButton = select(selectors.GALLERY_NEXT_SELECTOR, this)
    this.prevButton = select(selectors.GALLERY_PREV_SELECTOR, this)
    this.thumbnailNextButton = select(selectors.THUMBNAIL_NEXT_SELECTOR, this)
    this.thumbnailPrevButton = select(selectors.THUMBNAIL_PREV_SELECTOR, this)
    this.stockBadgeEl = select('[data-gallery-stock-badge]', this)
    this.warehouse1P = localStorage.getItem(STORAGE_1P_KEY) ?? null
    this.warehouse2P = localStorage.getItem(STORAGE_2P_KEY) ?? null
    this.currentVariant = window.productState?.initProduct?.initVariant ?? null
    // Bundle parents carry no warehouse blob of their own — the child SKUs in
    // linked_products.package_items are the only stock signal. Product-level, so it never changes
    // with the selected variant.
    this.bundleComposition = parseBundleComposition(window.productState?.initProduct?.metafields?.bundle_child_skus)
    this.isBundle = this.bundleComposition.length > 0
    this.bundleBadgeStatus = null
    this.bundleRequestToken = 0

    setTimeout(() => {
      this.removeAttrButtonCarousel(this.nextButton)
      this.removeAttrButtonCarousel(this.prevButton)
      this.syncThumbnailHeightToMainWidth()
      this.setupThumbnailNavigation()
      this.setupMainCarouselSync()
      this.setupStickyPositionSync()
    })
  }

  connectedCallback() {
    if (this.hasAttribute('data-enable-zoom')) {
      this.attachEvents()
    }

    this.setupVariantListener()
    this.setupModalZIndexHandler()
    this.setupStockBadgeListeners()
    this.updateStockBadge()
    this.refreshBundleBadge()
  }

  setupModalZIndexHandler() {
    if (!this.modalZoom) return

    const galleryWrapper = document.querySelector('.product-gallery-wrapper')
    if (!galleryWrapper) return

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          if (this.modalZoom.classList.contains('modal-dialog-opened')) {
            galleryWrapper.classList.remove('z-10')
            galleryWrapper.classList.add('z-50')
          } else {
            galleryWrapper.classList.remove('z-50')
            galleryWrapper.classList.add('z-10')
          }
        }
      })
    })

    observer.observe(this.modalZoom, {
      attributes: true,
      attributeFilter: ['class']
    })
  }

  setupStickyPositionSync() {
    const galleryWrapper = document.querySelector('.product-gallery-wrapper')
    const pageHeader = document.querySelector('.page-header.js-page-header')

    if (!galleryWrapper || !pageHeader) return

    const updateStickyPosition = () => {
      if (pageHeader.classList.contains('is-unpin') || pageHeader.classList.contains('is-pin')) {
        galleryWrapper.classList.remove('md:top-[11rem]')
        galleryWrapper.classList.add('md:top-[8rem]')
      } else {
        galleryWrapper.classList.remove('md:top-[8rem]')
        galleryWrapper.classList.add('md:top-[11rem]')
      }
    }

    updateStickyPosition()

    // Watch for class changes on header
    const observer = new MutationObserver(() => {
      updateStickyPosition()
    })

    observer.observe(pageHeader, {
      attributes: true,
      attributeFilter: ['class']
    })
  }

  syncThumbnailHeightToMainWidth() {
    if (!this.mainCarouselDesktop || !this.thumbnailCarousel) return

    const updateDimensions = () => {
      const mainCarouselWidth = this.mainCarouselDesktop.offsetWidth

      if (!mainCarouselWidth) {
        setTimeout(updateDimensions, 100)
        return
      }

      const thumbnailWrapper = this.thumbnailCarousel.closest('.w-24')
      if (thumbnailWrapper) {
        thumbnailWrapper.style.height = `${mainCarouselWidth}px`
        thumbnailWrapper.style.maxHeight = `${mainCarouselWidth}px`

        const thumbnailSlides = this.thumbnailCarousel.querySelectorAll('swiper-slide')
        thumbnailSlides.forEach(slide => {
          slide.style.height = '96px'
          slide.style.minHeight = '96px'
          slide.style.maxHeight = '96px'
        })

        const thumbnailHeight = 96
        const spaceBetween = 8
        const availableHeight = mainCarouselWidth
        const slidesPerView = Math.floor(availableHeight / (thumbnailHeight + spaceBetween))

        if (this.thumbnailCarousel.swiper) {
          this.thumbnailCarousel.swiper.params.slidesPerView = slidesPerView
          this.thumbnailCarousel.swiper.params.spaceBetween = spaceBetween
          this.thumbnailCarousel.swiper.update()
        }
      }
    }

    updateDimensions()
    window.addEventListener('resize', () => {
      updateDimensions()
    })
  }

  setupThumbnailNavigation() {
    if (!this.thumbnailCarousel || !this.thumbnailNextButton || !this.thumbnailPrevButton) return

    const tryAttachNavigation = (attempts = 0) => {
      const swiper = this.thumbnailCarousel.swiper

      if (!swiper && attempts < 5) {
        setTimeout(() => tryAttachNavigation(attempts + 1), 200)
        return
      }

      if (!swiper) return

      const updateButtonStates = () => {
        if (swiper.isBeginning) {
          this.thumbnailPrevButton.classList.add('swiper-button-disabled')
        } else {
          this.thumbnailPrevButton.classList.remove('swiper-button-disabled')
        }

        if (swiper.isEnd) {
          this.thumbnailNextButton.classList.add('swiper-button-disabled')
        } else {
          this.thumbnailNextButton.classList.remove('swiper-button-disabled')
        }
      }

      // Initial state
      updateButtonStates()

      // Update on slide change
      swiper.on('slideChange', updateButtonStates)

      // Update on carousel update (resize, etc)
      swiper.on('update', updateButtonStates)

      // Attach click events to custom navigation buttons
      on('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        swiper.slidePrev()
      }, this.thumbnailPrevButton)

      on('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        swiper.slideNext()
      }, this.thumbnailNextButton)
    }

    setTimeout(() => tryAttachNavigation(), 200)
  }

  setupMainCarouselSync() {
    if (!this.mainCarouselDesktop || !this.thumbnailCarousel) return

    const trySetupSync = (attempts = 0) => {
      const mainSwiper = this.mainCarouselDesktop.swiper
      const thumbSwiper = this.thumbnailCarousel.swiper

      if ((!mainSwiper || !thumbSwiper) && attempts < 5) {
        setTimeout(() => trySetupSync(attempts + 1), 200)
        return
      }

      if (!mainSwiper || !thumbSwiper) return

      mainSwiper.on('slideChange', () => {
        const activeIndex = mainSwiper.activeIndex

        if (thumbSwiper.slides[activeIndex]) {
          thumbSwiper.slideTo(activeIndex, 300)
        }
      })

      thumbSwiper.on('click', (swiper, event) => {
        const clickedIndex = swiper.clickedIndex
        if (clickedIndex !== undefined) {
          thumbSwiper.slideTo(clickedIndex, 300)
        }
      })
    }

    setTimeout(() => trySetupSync(), 300)
  }

  setupVariantListener() {
    if (!this.wishlistButton) return

    document.addEventListener('variant:changed', (event) => {
      if (event.detail && event.detail.variant) {
        this.wishlistButton.setAttribute('data-variant-id', event.detail.variant.id)
      }
    })
  }

  setupStockBadgeListeners() {
    this._onPostcodeUpdated = (e) => {
      this.warehouse1P = e.detail.warehouse1P ?? null
      this.warehouse2P = e.detail.warehouse2P ?? null
      this.updateStockBadge()
      this.refreshBundleBadge()
    }
    this._onVariantUpdated = (variant) => {
      this.currentVariant = variant
      this.updateStockBadge()
    }

    window.addEventListener('postcode:updated', this._onPostcodeUpdated)
    globalEmitter.on(emitterProps.product.updateVariant, this._onVariantUpdated)
  }

  // Resolves the bundle rollup from the child SKUs. The key matches what product-atc requests, so
  // the shared cache in helpers/bundle-items keeps this to one Storefront query per page.
  refreshBundleBadge() {
    if (!this.isBundle || !this.stockBadgeEl) return

    const token = ++this.bundleRequestToken

    getBundleStockRollup(this.bundleComposition, {
      warehouse1P: this.warehouse1P,
      warehouse2P: this.warehouse2P
    })
      .then((body) => {
        if (token !== this.bundleRequestToken) return
        this.bundleBadgeStatus = body?.badgeStatus ?? null
        this.updateStockBadge()
      })
      .catch((err) => {
        console.error('Some error getting the bundle gallery stock badge', err)
        if (token !== this.bundleRequestToken) return
        this.bundleBadgeStatus = null
        this.updateStockBadge()
      })
  }

  updateStockBadge() {
    if (!this.stockBadgeEl) return

    const BADGE_BASE = 'px-2 py-1.5 mb-2 rounded uppercase text-center text-white text-xs font-[550] badge-background'

    if (this.isBundle) {
      this.renderStockBadge(BUNDLE_BADGES[this.bundleBadgeStatus] ?? null, BADGE_BASE)
      return
    }

    if (!this.currentVariant) return

    const variantLike = { metafields: this.currentVariant.metafields }
    let badge = null

    const hasPostcode = this.warehouse1P !== null || this.warehouse2P !== null

    // Warehouse data is the sole authority here, matching plp-card — Shopify's own `available`
    // used to hide the badge for a deny-policy preorder variant the PLP card happily labelled.
    if (hasPostcode) {
      const activeWarehouse = resolveActiveWarehouse(this.warehouse1P, this.warehouse2P, variantLike)
      const metafieldKey = activeWarehouse ? getMetafieldKey(activeWarehouse) : null
      const rawStock = metafieldKey ? variantLike.metafields?.[metafieldKey] : null
      const stockData = rawStock ? parseStockJson(rawStock) : null

      if (stockData?.stockstatusmessage === 'instock' && isSellable(stockData)) {
        badge = { text: 'Ready to Ship', cls: 'badge-label-rts' }
      } else if (isPreorderStatus(stockData?.stockstatusmessage) && isSellable(stockData)) {
        badge = { text: 'Pre-order', cls: 'badge-label-preorder' }
      }
    } else {
      // No postcode — the best warehouse decides the badge (see combineWarehouseVerdicts).
      const combined = combineWarehouseVerdicts(
        classifyWarehouseStatus(parseStockJson(variantLike.metafields?.brisbane_stock_json)),
        classifyWarehouseStatus(parseStockJson(variantLike.metafields?.melbourne_stock_json))
      )

      if (combined === 'instock') {
        badge = { text: 'Ready to Ship', cls: 'badge-label-rts' }
      } else if (combined === 'preorder') {
        badge = { text: 'Pre-order', cls: 'badge-label-preorder' }
      }
    }

    this.renderStockBadge(badge, BADGE_BASE)
  }

  renderStockBadge(badge, baseClass) {
    if (badge) {
      this.stockBadgeEl.innerHTML = `<div class="${baseClass} ${badge.cls}">${badge.text}</div>`
    } else {
      this.stockBadgeEl.innerHTML = ''
    }
  }

  attachEvents() {
    setTimeout(() => {
      this.attachCarouselEvents(this.mainCarouselDesktop)
      this.attachCarouselEvents(this.mainCarouselMobile)
    }, 300)
  }

  attachCarouselEvents(carousel) {
    if (!carousel) return

    const galleryItems = selectAll(selectors.GALLERY_ITEM_SELECTOR, carousel)

    map((item, index) => {
      const img = item.querySelector('img')
      const videoPlayer = item.querySelector('video-player')

      if (img && !videoPlayer) {
        on(
          'click',
          () => {
            if (this.modalZoom) {
              this.modalZoom.show()
              this.setMainActive(index)
            }
          },
          item
        )
      }
    }, galleryItems)
  }

  setMainActive(index) {
    if (this.mainGalleryZoom && this.mainGalleryZoom.swiper) {
      this.mainGalleryZoom.swiper.slideTo(index, 0)
    }
  }

  removeAttrButtonCarousel(el) {
    el && removeAttribute('aria-controls', el)
    el && removeAttribute('tabindex', el)
  }
}

customElements.define('product-gallery', ProductGallery)
